import React, { useEffect, useRef, useState } from 'react';
import { SkeletonGroup, SkeletonThumbnail } from '@shared/components/Skeleton';
import type { LazyScreenProps } from './LazyScreen.types';
import { reportImageLoadFailure } from '@shared/utils/reportImageLoadFailure';

export const LazyScreen: React.FC<LazyScreenProps> = props => {
  const { height, width, children, src, aspectRatio, showSkeleton, skeletonGroupOverrides } = props;
  const initializedRef = useRef(false);
  // Start loaded=true when showSkeleton is explicitly false (e.g., above-the-fold SSR content).
  // This prevents the skeleton from being SSR'd for header photos where ProgressiveImage
  // already handles its own loading state.
  const [loaded, setLoaded] = useState(src === '' || showSkeleton === false);

  const handleOnLoad = () => {
    setLoaded(true);
  };

  useEffect(() => {
    // When showSkeleton is explicitly false, loaded is already true and children
    // render immediately — skip the redundant Image() preload to avoid duplicate
    // network requests for the same URL.
    if (showSkeleton === false) return;

    if (!src) {
      // if no src, set loaded to true to render children
      handleOnLoad();
      return;
    }

    if (initializedRef.current) {
      return;
    }

    initializedRef.current = true;

    const img = new Image();

    img.onload = handleOnLoad;
    // if the image fails to load, set loaded to true to render children
    // otherwise, the skeleton will be shown indefinitely
    img.onerror = () => {
      reportImageLoadFailure({ component: 'LazyScreen', src });
      handleOnLoad();
    };

    img.src = src;

    return () => {
      img.onload = null;
      img.onerror = null;
      img.src = '';
    };
  }, [src, showSkeleton]);

  return (
    <SkeletonGroup
      isReady={loaded && !showSkeleton}
      overrides={skeletonGroupOverrides}
      placeholder={
        <SkeletonThumbnail
          height={height}
          width={width}
          aspectRatio={aspectRatio}
        />
      }
    >
      {children(loaded)}
    </SkeletonGroup>
  );
};

LazyScreen.displayName = 'LazyScreen';
